import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../provider';
import { useNavigate } from "react-router-dom";
import { Grid, Box, CircularProgress, Typography, Chip, Stack, Divider, Paper, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as c from '../../../constants';
import CustomDialogSmall from '../../../components/Dialog/DiaCompSmall';
import TextInput from '../../../components/Forms/TextInput';
import DateInput from '../../../components/Forms/DateInput';
import SelectInputNew from '../../../components/Forms/SelectInputNew';
import ButtonForm from '../../../components/Button/ButtonForm';
import { Formik, Form } from "formik";
import Alert from "@mui/material/Alert";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import dayjs from "dayjs";
import ButtonsMain from '../../../components/Button/ButtonMain';
import { useTranslation } from '../../../language';
import { Tablets, Calendar, Trash2, Pencil, PlusCircle } from 'lucide-react';

export default function MedicationPanel() {

  const { state } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  const apiUrlList = `${c.API_URL}/api/medication-list/`;
  const apiUrlItem = `${c.API_URL}/api/medication-item/`;

  const [medList, setMedList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState();
  const [error, setError] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogDel, setOpenDialogDel] = useState(false);
  const [openDialogAdd, setOpenDialogAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // ----- Fetch Medication List -----
  const fetchMedList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiUrlList);
      setMedList(response.data[0] || { items: [] });
      setError(null);
    } catch (err) {
      console.error(err);
      setError(t("med_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedList();
  }, [fetchMedList]);

  // ----- Handlers -----
  const handleEdit = (item) => { setEditingRow(item); setOpenDialog(true); };
  const handleDelete = (item) => { setEditingRow(item); setOpenDialogDel(true); };
  const handleAdd = () => { setEditingRow(null); setOpenDialogAdd(true); };

  const handleUpdateSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const response = await axios.put(`${apiUrlItem}${editingRow.id}/`, values);
      if ([200, 201].includes(response.status)) {
        setMessage(t("med_updated"));
        setOpenDialog(false);
        fetchMedList();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t("med_update_error"));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDeleteSubmit = async (_, { setSubmitting }) => {
    setLoading(true);
    try {
      const response = await axios.delete(`${apiUrlItem}${editingRow.id}/`);
      if ([200, 201, 204].includes(response.status)) {
        setMessage(t("med_deleted"));
        setOpenDialogDel(false);
        fetchMedList();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t("med_delete_error"));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleAddSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(apiUrlItem, values);
      if (response.status === 201) {
        setMessage(t("med_added"));
        setOpenDialogAdd(false);
        resetForm();
        await fetchMedList();
      }
    } catch (err) {
      console.error("FULL ERROR RESPONSE:", err.response?.data);
      setError(JSON.stringify(err.response?.data));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const cardStyle = {
    borderRadius: 4, border: '1px solid',
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 6 }, pb: 6 }}>

      {/* ── Header ── */}
      <Stack spacing={2} sx={{ mb: 3, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{t("med_headline")}</Typography>
          <Typography variant="body1" color="text.secondary">{t("med_intro")}</Typography>
        </Box>
        <ButtonsMain
          action={handleAdd}
          name={t("med_add_btn")}
          startIcon={<PlusCircle size={16} />}
          sx={{
            px: { xs: '24px', sm: '16px', lg: '10px' },
            fontSize: { lg: '0.75rem' },
          }}
        />
      </Stack>

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ── Loading ── */}
      {loading && (
        <Box display="flex" alignItems="center" gap={2} sx={{ my: 4 }}>
          <CircularProgress size={24} />
          <Typography color="text.secondary">{t("med_fetch_loading")}</Typography>
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && (medList?.items || []).length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', ...cardStyle, border: '1px dashed' }}>
          <Tablets size={48} color={theme.palette.text.disabled} style={{ marginBottom: 12 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>{t("med_no_items")}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <ButtonsMain action={handleAdd} name={t("med_add_btn")} startIcon={<PlusCircle size={16} />} />
          </Box>
        </Paper>
      )}

      {/* ── Medication list ── */}
      {!loading && (medList?.items || []).length > 0 && (
        <Paper sx={{ overflow: 'hidden', ...cardStyle }}>
          {medList.items.map((item, idx) => (
            <Box key={item.id}>
              {idx > 0 && <Divider />}
              <MedListRow
                item={item}
                idx={idx}
                onEdit={() => handleEdit(item)}
                onDelete={() => handleDelete(item)}
                t={t}
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* ── Update Dialog ── */}
      {editingRow && (
        <CustomDialogSmall
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          title={t("med_dia_update")}
          content={
            <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <MedicationForm initialValues={editingRow} onSubmit={handleUpdateSubmit} />
              </Grid>
            </Grid>
          }
        />
      )}

      {/* ── Delete Dialog ── */}
      {editingRow && (
        <CustomDialogSmall
          open={openDialogDel}
          onClose={() => setOpenDialogDel(false)}
          title={t("med_dia_delete")}
          content={<DeleteConfirmForm onSubmit={handleDeleteSubmit} />}
        />
      )}

      {/* ── Add Dialog ── */}
      <CustomDialogSmall
        open={openDialogAdd}
        onClose={() => setOpenDialogAdd(false)}
        title={t("med_dia_add")}
        content={
          <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <MedicationForm
                initialValues={{ drugname: "", indication: "", dosing: "", route: "", frequency: "", start_date: null, end_date: null, status: "active", notes: "" }}
                onSubmit={handleAddSubmit} loading={loading} message={message} error={error}
              />
            </Grid>
          </Grid>
        }
      />
    </Box>
  );
}

// ─────────────────────────────────────────────
// Medication Row (list view)
// ─────────────────────────────────────────────
function MedListRow({ item, idx, onEdit, onDelete, t }) {
  const theme = useTheme();
  const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        px: 2,
        py: 1.5,
        borderLeft: `10px solid ${accentColor}`,
        transition: 'background-color 0.15s',
        '&:hover': {
          bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
        },
      }}
    >
      {/* Icon */}
      <Box sx={{
        width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
        bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Tablets size={20} color={accentColor} />
      </Box>

      {/* Drug name + indication */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" fontWeight={600} noWrap title={item.drugname}>
          {item.drugname || '—'}
        </Typography>
        {item.indication && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {item.indication}
          </Typography>
        )}
      </Box>

      {/* Status chip */}
      {item.status && (
        <Chip
          size="small"
          variant="outlined"
          label={
            <Typography variant="caption" sx={{
              color: item.status === 'active' ? 'common.white' : 'inherit',
              textTransform: 'uppercase',
              fontWeight: 'bold',
            }}>
              {item.status}
            </Typography>
          }
          sx={{
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
            ...(item.status === 'active' ? { bgcolor: 'traffic.green' } : {}),
          }}
        />
      )}

      {/* Date range */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' }, minWidth: 180 }}>
        <Calendar size={16} color={accentColor} />
        <Typography variant="caption" fontWeight={600}>
          {item.start_date || '—'}{item.end_date ? ` → ${item.end_date}` : ''}
        </Typography>
      </Stack>

      {/* Dose / route / frequency chips */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, display: { xs: 'none', lg: 'flex' } }}>
        {item.dosing    && <Chip label={item.dosing}     size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
        {item.route     && <Chip label={item.route}      size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
        {item.frequency && <Chip label={item.frequency}  size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
      </Stack>

      {/* Actions */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <ButtonsMain action={onEdit} name={t("med_col_update")} startIcon={<Pencil size={14} />} />
        <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }}>
          <Trash2 size={16} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

// ----- Form Components -----
function MedicationForm({ initialValues, onSubmit, loading, message, error }) {
  const { t } = useTranslation();
  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmit}>
      {({ values, setFieldValue, isSubmitting }) => (
        <Form>
          <TextInput label={t("med_form_drug")}         name="drugname" />
          <TextInput label={t("med_form_indication")}   name="indication" />
          <TextInput label={t("med_form_dosing")}       name="dosing" />
          <TextInput label={t("med_form_route")}        name="route" />
          <TextInput label={t("med_form_frequency")}    name="frequency" />

          <DateInput
            label={t("med_form_start")}
            name="start_date"
            value={values.start_date ? dayjs(values.start_date) : null}
            onChange={date => setFieldValue("start_date", date ? dayjs(date).format("YYYY-MM-DD") : null)}
          />
          <DateInput
            label={t("med_form_end")}
            name="end_date"
            value={values.end_date ? dayjs(values.end_date) : null}
            onChange={date => setFieldValue("end_date", date ? dayjs(date).format("YYYY-MM-DD") : null)}
          />

          <SelectInputNew
            label={t("med_form_status")}
            name="status"
            options={[
              { value: 'active',       label: t("med_status_active") },
              { value: 'inactive',     label: t("med_status_inactive") },
              { value: 'discontinued', label: t("med_status_discontinued") },
            ]}
          />

          <TextInput label={t("med_form_notes")} name="notes" />

          <ButtonForm name={t("med_form_submit")} disabled={isSubmitting || loading} />

          {loading  && <span>{t("med_uploading")}</span>}
          {message  && <Alert icon={<EmojiEmotionsIcon />} severity="success" sx={{ mt: 2 }}>{message}</Alert>}
          {error    && <Alert icon={<ReportProblemIcon />} severity="error"   sx={{ mt: 2 }}>{error}</Alert>}
        </Form>
      )}
    </Formik>
  );
}

function DeleteConfirmForm({ onSubmit, loading, message, error }) {
  const { t } = useTranslation();
  return (
    <Formik initialValues={{}} onSubmit={onSubmit}>
      {({ isSubmitting }) => (
        <Form>
          <Typography sx={{ mb: 2 }}>{t("med_confirm_delete")}</Typography>
          <ButtonForm name={t("med_btn_confirm_delete")} disabled={isSubmitting || loading} />
          {message && <Alert icon={<EmojiEmotionsIcon />} severity="success" sx={{ mt: 2 }}>{message}</Alert>}
          {error   && <Alert icon={<ReportProblemIcon />} severity="error"   sx={{ mt: 2 }}>{error}</Alert>}
        </Form>
      )}
    </Formik>
  );
}
